import { IPsm } from '@turnercode/types'
import ActiveConfig from './activeConfig'
import {
  PsmVideoPlayer,
  IBridge,
  ObjectMap,
  StringMap,
  IPlayer,
  ITimer,
  IManagerConfig,
  IActiveConfig,
  DeviceMetadataWebType,
  DeviceMetadataReactNativeType,
  IMetaData,
  ProgramMapType
} from './interfaces'
import { getKeysFromEventMap, logger, secondsInMilliseconds } from '../utils'
import PsmMgrConfig from './psm-mgrConfig'
import PsmTimer from './psm-timer'
import { PSM_MGR_POLL_INTERVAL_MIN } from './constants'
import PsmMetadata from './psm-metadata'
import PsmEventBuilder from './psm-event-builder'

class PsmBridge implements IBridge { 
  psmProductConfig: IManagerConfig
  psmProgramMetadata: ObjectMap = {}
  pollingTimer: ITimer | undefined
  psmActiveConfig: IActiveConfig
  metadata: IMetaData<DeviceMetadataWebType | DeviceMetadataReactNativeType>
  videoPlayer: PsmVideoPlayer
  static psmActiveConfigEvents: string[]
  psmMetadata: PsmMetadata<DeviceMetadataWebType | DeviceMetadataReactNativeType>
  eventBuilder: PsmEventBuilder
  static dependencies: any // TODO: Add an interface
  playerDependencies: any
  metadataDependencies: { getMetadata: (playerDependencies: any) => PsmMetadata<DeviceMetadataWebType | DeviceMetadataReactNativeType> }
  static psm: IPsm
  static platform: string
  static isThrottlingRequested: boolean
  static isThrottled: boolean = false
  isBridgeInitialized: boolean = false
  mappedProgramMetadata: ObjectMap = {}

  constructor(videoPlayer: IPlayer, psm: IPsm, dependencies: any, playerDependencies: any, metadataDependencies: { getMetadata: (playerDependencies: any) => PsmMetadata<DeviceMetadataWebType | DeviceMetadataReactNativeType> }) {
    PsmBridge.dependencies = dependencies
    this.playerDependencies = playerDependencies
    this.metadataDependencies = metadataDependencies
    PsmBridge.psm = psm
    this.videoPlayer = videoPlayer
    this.metadata = new PsmMetadata(playerDependencies)
    this.psmActiveConfig = new ActiveConfig()
    this.psmProductConfig = new PsmMgrConfig()
    this.setConfig()
    this.eventBuilder = new PsmEventBuilder(this)
    const sendPsmEventCallback = (eventName: string): void => {
      this.eventBuilder.queuePsmVidEvent(eventName)
    }
    this.videoPlayer.activateListeners(this.eventBuilder, sendPsmEventCallback)
  }

  async setConfig(): Promise<void> {
    const isNewManagerConfigReady = await this.psmProductConfig.getAndCheckPsmMgrConfigCDN()
    await this.psmActiveConfig.getLocalStorageConfig()

    const schemaVersionFromManagerConfig = this.psmProductConfig?.psmMgrConfig?.psmTelSchemaVersion
    const doesCurrentVersionMatchSavedVersion = ActiveConfig.currentVersion === schemaVersionFromManagerConfig

    if (isNewManagerConfigReady || !doesCurrentVersionMatchSavedVersion) {
      if (!this.pollingTimer) {
        this.setPollingTimerForThrottling()
      }

      if (this.pollingTimer.interval !== secondsInMilliseconds(+PSM_MGR_POLL_INTERVAL_MIN * 60)) {
        this.pollingTimer.updateInterval(+PSM_MGR_POLL_INTERVAL_MIN * 60)
      }

      const isNewVersionReadyToSave = !doesCurrentVersionMatchSavedVersion && isNewManagerConfigReady
      if (!this.isBridgeInitialized || isNewVersionReadyToSave) {
        const newSchemaVersion = schemaVersionFromManagerConfig || ActiveConfig.activeConfig.version
        await this.psmActiveConfig.updateLocalStorageConfig(newSchemaVersion)
        this.updateProgramMapStructure(ActiveConfig.activeConfig.psmProgramMap)
        this.isBridgeInitialized = true
      }
      this.setMetadataListener(ActiveConfig.activeConfig.psmProgramMap)
      this.setEventsToMonitor(ActiveConfig.activeConfig.eventMap)
    }
    this.updateThrottling()
  }

  setEventsToMonitor(eventMap: StringMap): void {
    PsmBridge.psmActiveConfigEvents = getKeysFromEventMap(eventMap)
  }

  updateProgramMapStructure(keyMap: ProgramMapType): void {
    const values = this.videoPlayer.asyncMetadata.unmapped
    this.mappedProgramMetadata = this.metadata.mappingMetadata(values, keyMap, this.playerDependencies)
  }

  setMetadataListener(keyMap: ProgramMapType): void {
    this.psmMetadata = this.metadataDependencies.getMetadata(this.playerDependencies)
    const self = this
    const handler = {
      set(target: any, prop: string, value: any) {
        if (prop === 'unmapped') { 
          self.mappedProgramMetadata = self.metadata.mappingMetadata(value, keyMap, self.playerDependencies)
        }
        return (target[ prop ] = value)
      },
    }
    this.videoPlayer.asyncMetadata = new Proxy(this.psmProgramMetadata, handler)
  }

  updateThrottling() {
    PsmBridge.isThrottlingRequested = this.psmProductConfig.getThrottleFlag()
    PsmBridge.isThrottled = PsmBridge.isThrottlingRequested
    logger('Events are Throttling now?', PsmBridge.isThrottlingRequested)
  }

  setPollingTimerForThrottling() {
    //TODO: PSM_MGR_POLL_INTERVAL_MIN will be polling from psm.json CDN url
    this.pollingTimer = new PsmTimer(this.setConfig.bind(this), +PSM_MGR_POLL_INTERVAL_MIN * 60)
    this.pollingTimer.start()
  }
}

export default PsmBridge
